/**
 * Network Page - Trade Loop v2.0
 *
 * Two tabs: My Partners, Find New Partners
 *
 * Features:
 * - My Partners: Search by name and trade, clickable partner cards
 * - Find New Partners: Search all users, send connection request with message
 * - QR code/link invite feature
 */

import { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { serviceAreasOverlap } from "@/lib/service-area-matching";
import { useTradeNames } from "@/hooks/useTrades";
import { useMarketTypeNames } from "@/hooks/useMarketTypes";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeConnections, useRealtimeSuggestedPartners } from "@/hooks";
import { useSubscription } from "@/hooks/useSubscription";
import AppLayout from "@/components/layout/AppLayout";
import { useToast } from "@/contexts/ToastContext";
import ProfilePreviewModal from "@/components/ProfilePreviewModal";
import { UpgradePrompt, PartnerFavoritesUpgradeCard } from "@/components/subscription";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
} from "@/components/ui/avatar";
import {
  UserPlus,
  MessageCircle,
  Star,
  Send,
  Search,
  X,
  Heart,
  Sparkles,
  QrCode,
  Check,
  ChevronRight,
  Users,
  Building2,
  Loader2,
  Share2,
  ArrowUpDown,
  Clock,
  UserCheck,
  MoreVertical,
  Ban,
  ShieldOff,
  MapPin,
} from "lucide-react";
import { useBlockUser, useUnblockUser } from "@/api/extensions/useBlockUser";
import NetworkHealthCard from "@/components/NetworkHealthCard";
import ProBadge from "@/components/ProBadge";
import { shareableUrl } from "@/utils/shareableUrl";
import DemoPartnersStrip from "@/components/getting-started/DemoPartnersStrip";
import { useGettingStarted } from "@/components/getting-started/GettingStartedContext";

// Types for v2.0
interface Connection {
  id: string;
  connectionId?: string;
  firstName: string;
  lastName: string;
  company?: string;
  primaryTrade?: string;
  profileImageUrl?: string;
  isFavorite?: boolean;
  status?: "accepted" | "pending";
  [key: string]: any;
}

interface Partner {
  id: string;
  firstName: string;
  lastName: string;
  company?: string;
  primaryTrade?: string;
  trades?: string[];
  serviceAreas?: string[];
  /**
   * County-keys covering each entry in {@link serviceAreas}. Supplied by the
   * server (partner-suggestions workflow) so the client can perform
   * county↔city containment matching without bundling the reference dataset.
   * Each key is `${countyLower}|${stateCodeUpper}`, e.g. `fulton|GA`.
   */
  serviceAreaCountyKeys?: string[];
  marketTypes?: string[];
  location?: string;
  profileImageUrl?: string;
  subscriptionPlan?: string;
  roleName?: string | null;
  completedReferralsCount?: number;
  [key: string]: any;
}

interface PendingConnection {
  id: string;
  userId: string;
  connectedUserId: string;
  status: string;
  createdAt: string;
  isIncoming: boolean;
  otherUser: {
    id: string;
    firstName: string;
    lastName: string;
    company?: string;
    trade?: string;
    profileImageUrl?: string;
  } | null;
}

/**
 * Stable DOM id for a partner-list group header, so the Network Health modal can deep-link
 * to a trade with `/network?category=<trade name>`. Trade names are the only identifier the
 * grouped partner list has (groups are keyed by `partner.primaryTrade`), so the slug is
 * derived from the name rather than a category id.
 */
export function tradeGroupAnchorId(tradeName: string): string {
  const slug = tradeName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `trade-group-${slug}`;
}

export default function NetworkPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSuccess, showError } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { isAtLimit, canPerformAction, featureFlags } = useSubscription();

  // Redirect team members (technician/member) - they cannot access network page
  useEffect(() => {
    if (user?.organizationMembership) {
      const role = user.organizationMembership.role;
      if (role === 'member') {
        showError('Access Denied', 'Team members cannot access the network page');
        navigate('/organization', { replace: true });
      }
    }
  }, [user, navigate, showError]);

  // Subscribe to Realtime connections for instant updates
  useRealtimeConnections({
    userId: user?.id,
    enabled: !!user,
  });

  // Subscribe to Realtime user profiles for suggested partners updates
  useRealtimeSuggestedPartners({
    enabled: !!user,
  });

  // Fetch trades from database
  const { data: trades = [], isLoading: tradesLoading } = useTradeNames();

  // Fetch market types from database
  const { data: marketTypes = [], isLoading: marketTypesLoading } = useMarketTypeNames();

  // Pull-to-refresh functionality - AppLayout handles toasts
  const handleRefresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["/api/workflows/connections"] }),
      queryClient.invalidateQueries({ queryKey: ["/api/partners/suggested"] }),
    ]);
  };

  // While the "partners" walkthrough is active, land the user on Find New
  // Partners so the fictional demo strip and the tour coachmark are visible.
  const { activeTrack: gettingStartedTrack } = useGettingStarted();

  // State - initialize tab from URL search params
  const tabParam = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<
    "partners" | "suggestions"
  >(tabParam === 'suggestions' || gettingStartedTrack === 'partners' ? 'suggestions' : 'partners');

  useEffect(() => {
    if (gettingStartedTrack === 'partners') {
      setActiveTab('suggestions');
    }
  }, [gettingStartedTrack]);

  // Sync tab changes to URL
  const handleTabChange = (tab: "partners" | "suggestions") => {
    setActiveTab(tab);
    if (tab === 'suggestions') {
      setSearchParams({ tab: 'suggestions' });
    } else {
      // Remove tab param when going to partners (default)
      searchParams.delete('tab');
      setSearchParams(searchParams);
    }
  };

  // Update tab when URL changes (e.g., navigating from another page)
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl === 'suggestions' && activeTab !== 'suggestions') {
      setActiveTab('suggestions');
    } else if (tabFromUrl !== 'suggestions' && activeTab === 'suggestions') {
      // Only reset if explicitly navigating without tab param
      // Don't reset when user manually clicks tab
    }
  }, [searchParams]);
  const [searchQuery, setSearchQuery] = useState("");
  const [tradeFilter, setTradeFilter] = useState<string>("all");
  const [locationFilter, setLocationFilter] = useState("");
  const [partnerSortBy, setPartnerSortBy] = useState<string>("favorites");
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  // Deep link from the Network Health modal: `/network?category=<trade name>` opens
  // My Partners and scrolls to that trade's group of partners.
  const categoryParam = searchParams.get('category');

  // Mirrors `deepLinkListReady` (defined below, once the queries it reads are declared) so
  // the animation-frame loop can read the live value without the scroll effect re-running
  // on every change.
  const deepLinkListReadyRef = useRef(false);

  useEffect(() => {
    if (!categoryParam) return;

    setActiveTab('partners');
    // Grouping by trade is what makes a per-trade section exist at all — under the default
    // "Favorites First" sort a favorited partner is filed under Favorites instead of their
    // trade, so the target group could be missing or incomplete.
    setPartnerSortBy('trade-asc');

    const targetId = tradeGroupAnchorId(categoryParam);
    // ~8s of frames. The budget has to cover cold API calls now that the scroll waits for
    // them to finish, not just the render of an already-cached list.
    const MAX_ATTEMPTS = 480;
    let attempts = 0;
    let frame = 0;

    const clearCategoryParam = () => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('category');
          return next;
        },
        { replace: true }
      );
    };

    const jumpTo = (target: HTMLElement) => {
      // Lands below the fixed header: <main> carries scroll-padding-top of the real
      // header height (AppLayout), and the group adds a little breathing room on top.
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      target.focus({ preventScroll: true });
      clearCategoryParam();
    };

    // Nothing may move until the list is final — regrouped by trade and holding every
    // response. The ref only turns true on a commit that already contains that layout, so by
    // the time this loop sees it neither the target's position nor its existence can change.
    const scrollToGroup = () => {
      const target = document.getElementById(targetId);
      if (deepLinkListReadyRef.current) {
        // The list is final: either the group is there and we scroll to it, or the user has
        // no connected partners in this trade and we land on My Partners without scrolling
        // rather than surfacing an error.
        if (target) jumpTo(target);
        else clearCategoryParam();
        return;
      }
      if (++attempts >= MAX_ATTEMPTS) {
        // Out of budget with something still in flight. Scroll if the group rendered
        // anyway — a slightly imprecise landing beats not moving at all.
        if (target) jumpTo(target);
        else clearCategoryParam();
        return;
      }
      frame = requestAnimationFrame(scrollToGroup);
    };

    frame = requestAnimationFrame(scrollToGroup);
    return () => cancelAnimationFrame(frame);
  }, [categoryParam, setSearchParams]);

  // Find Partners state
  const [findSearchQuery, setFindSearchQuery] = useState("");
  const [findTradeFilter, setFindTradeFilter] = useState<string>("all");
  const [findServiceAreaFilter, setFindServiceAreaFilter] = useState("");
  const [findMarketTypeFilter, setFindMarketTypeFilter] = useState<string>("all");

  // Connection request dialog state
  const [connectDialogOpen, setConnectDialogOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [connectionMessage, setConnectionMessage] = useState("");


  // Profile preview modal state
  const [profilePreviewOpen, setProfilePreviewOpen] = useState(false);
  const [selectedProfilePartner, setSelectedProfilePartner] = useState<Partner | Connection | null>(null);

  // Block dialog state
  const [blockDialogOpen, setBlockDialogOpen] = useState(false);
  const [userToBlock, setUserToBlock] = useState<{ id: string; name: string } | null>(null);

  // Recommended partners modal state
  const [recommendedModalOpen, setRecommendedModalOpen] = useState(false);

  // All Partners In Your Area filter state
  const [allPartnersSortBy, setAllPartnersSortBy] = useState<string>("a-z");
  const [allPartnersTradeFilter, setAllPartnersTradeFilter] = useState<string>("all");
  const [allPartnersServiceAreaFilter, setAllPartnersServiceAreaFilter] = useState<string>("all");

  // Get the invite link
  // Public origin — this link is sent to someone who does not have the app yet, so a
  // capacitor://localhost URL would be unopenable. See utils/shareableUrl.
  // Users copy this and send it to other people, so it outlives any refactor —
  // /signup still redirects here for every link already in the wild.
  const inviteLink = shareableUrl(`/login?mode=signup&ref=${user?.id || ""}`);


  // Fetch connections (user's partners)
  // Realtime handles updates; no polling needed
  const { data: connections = [], isPending: connectionsLoading } = useQuery({
    queryKey: ["/api/workflows/connections"],
    queryFn: () => api.get("/api/workflows/connections"),
    enabled: !!user,
    refetchOnMount: 'always', // Always refetch when navigating to this page
    refetchOnWindowFocus: true, // Refetch when user returns to the tab
    staleTime: 0, // Always consider data stale
  });

  // Fetch suggested partners. Always refetch when the user lands on /network
  // (or refocuses the tab) so a partner who accepted/declined their request
  // while the page was idle disappears from the list without a manual reload.
  // Realtime invalidations from useRealtimeConnections cover the in-session
  // case; these options cover navigation and tab-focus.
  //
  // Gated on the active tab: this is the slowest request on the page (~1s TTFB) and
  // everything downstream of it — filteredSuggestedPartners, the recommended strip, the
  // "See all" modal — renders only inside the Suggestions tab, so on the default My
  // Partners landing it was a second of server work nobody could see. It does not gate the
  // `?category=` deep-link scroll either (deepLinkListReady deliberately excludes it), so
  // deferring it cannot delay that.
  const { data: suggestedPartners = [], isPending: suggestedLoading } = useQuery({
    queryKey: ["/api/partners/suggested"],
    queryFn: () => api.get("/api/partners/suggested"),
    enabled: !!user && !searchQuery && activeTab === 'suggestions', // Only fetch on the tab that renders it, and not while searching
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  // Fetch pending connection requests
  const { data: pendingConnections = [], isPending: pendingRequestsLoading } = useQuery({
    queryKey: ["/api/workflows/connections/pending"],
    queryFn: () => api.get("/api/workflows/connections/pending"),
    enabled: !!user,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  // Fetch partner network progress to identify unfilled trade categories.
  // NetworkHealthCard reads this same key, so its fetch is covered by this pending flag.
  const { data: networkProgress, isPending: networkProgressLoading } = useQuery({
    queryKey: ["/api/workflows/partner-network-progress"],
    queryFn: () => api.get("/api/workflows/partner-network-progress"),
    enabled: !!user,
  });

  // Gate for the `?category=` deep-link scroll. Two independent things must have landed.
  //
  // 1. The regrouping the deep-link effect itself asks for. It sets partnerSortBy to
  //    'trade-asc', and until that render commits the list is still under the default
  //    "Favorites First" grouping — a different layout that also contains ids of the form
  //    trade-group-*, so the target both moves and can be missing. On a warm cache the first
  //    animation frame beats that re-render, and the two failures look unrelated: an anchor
  //    present in both groupings (HVAC) scrolled to its pre-regroup offset and ended up under
  //    the header, while one filed under Favorites (a favourited partner's trade) was absent
  //    entirely, so the effect concluded "no partners in this trade" and gave up for good.
  //    Reading the state is enough — this flag reaches the scroll through a ref written in an
  //    effect, so it can only turn true on a commit whose DOM already holds the new grouping.
  //
  // 2. Every query that shapes the layout above the target holds data. Deliberately "has
  //    data", not "nothing is in flight" — these queries are refetchOnMount: 'always', so an
  //    in-flight check would block on a background refetch on every visit even though the
  //    cached list is already on screen and final. isPending is exactly that question (no
  //    data and no error yet), and it covers NetworkHealthCard too, which shares the progress
  //    query's key.
  const deepLinkListReady =
    partnerSortBy === 'trade-asc' &&
    !connectionsLoading &&
    !pendingRequestsLoading &&
    !networkProgressLoading &&
    !tradesLoading;

  useEffect(() => {
    deepLinkListReadyRef.current = deepLinkListReady;
  }, [deepLinkListReady]);

  // Separate incoming and outgoing pending requests
  const incomingRequests = useMemo(() => {
    const pendingArray = Array.isArray(pendingConnections) ? pendingConnections : [];
    return pendingArray.filter((conn: PendingConnection) => conn.isIncoming);
  }, [pendingConnections]);

  const outgoingRequests = useMemo(() => {
    const pendingArray = Array.isArray(pendingConnections) ? pendingConnections : [];
    return pendingArray.filter((conn: PendingConnection) => !conn.isIncoming);
  }, [pendingConnections]);

  // Get unique trades from connections
  const uniqueTrades = useMemo(() => {
    const connectionsArray = Array.isArray(connections) ? connections : [];
    const trades = new Set<string>();
    connectionsArray.forEach((partner) => {
      if (partner.primaryTrade) {
        trades.add(partner.primaryTrade);
      }
    });
    return Array.from(trades).sort();
  }, [connections]);

  // Filter partners based on search, trade, and favorites
  const filteredPartners = useMemo(() => {
    const connectionsArray = Array.isArray(connections) ? connections : [];
    // IMPORTANT: Only show connected partners (exclude blocked users)
    let filtered = connectionsArray.filter((conn) => conn.status === 'connected');

    // Search filter - includes name, company, and location
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((partner) => {
        const fullName =
          `${partner.firstName} ${partner.lastName}`.toLowerCase();
        const company = (partner.company || "").toLowerCase();
        const location = (partner.location || "").toLowerCase();

        // Check name or company match
        if (fullName.includes(query) || company.includes(query)) {
          return true;
        }

        // Check location field
        if (location.includes(query)) {
          return true;
        }

        // Check serviceAreas array
        if (partner.serviceAreas && Array.isArray(partner.serviceAreas)) {
          if (partner.serviceAreas.some((area: string) =>
            area.toLowerCase().includes(query)
          )) {
            return true;
          }
        }

        return false;
      });
    }

    // Trade filter
    if (tradeFilter && tradeFilter !== "all") {
      filtered = filtered.filter(
        (partner) => partner.primaryTrade?.toLowerCase() === tradeFilter.toLowerCase()
      );
    }

    // Location filter now handled by searchQuery above

    // Favorites filter
    if (showFavoritesOnly) {
      filtered = filtered.filter((partner) => partner.isFavorite);
    }

    // Sort based on selected option
    filtered.sort((a, b) => {
      // For favorites sort, favorites always come first
      if (partnerSortBy === "favorites") {
        if (a.isFavorite && !b.isFavorite) return -1;
        if (!a.isFavorite && b.isFavorite) return 1;
        // Both same favorite status - sort alphabetically
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return nameA.localeCompare(nameB);
      }

      // Sort by name
      if (partnerSortBy === "name-asc" || partnerSortBy === "name-desc") {
        const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
        const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
        return partnerSortBy === "name-asc"
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      }

      // Sort by trade
      if (partnerSortBy === "trade-asc" || partnerSortBy === "trade-desc") {
        const tradeA = (a.primaryTrade || "").toLowerCase();
        const tradeB = (b.primaryTrade || "").toLowerCase();
        // Partners without a trade go to the end
        if (!tradeA && tradeB) return 1;
        if (tradeA && !tradeB) return -1;
        return partnerSortBy === "trade-asc"
          ? tradeA.localeCompare(tradeB)
          : tradeB.localeCompare(tradeA);
      }

      return 0;
    });

    return filtered;
  }, [connections, searchQuery, tradeFilter, showFavoritesOnly, partnerSortBy]);

  // Group filtered partners for display with section headers
  const groupedPartners = useMemo(() => {
    if (filteredPartners.length === 0) return [];

    const groups: { header: string; partners: typeof filteredPartners }[] = [];

    if (partnerSortBy === "favorites") {
      // First, show favorites
      const favorites = filteredPartners.filter(p => p.isFavorite);
      if (favorites.length > 0) {
        groups.push({ header: "Favorites", partners: favorites });
      }

      // Then organize non-favorites by trade
      const nonFavorites = filteredPartners.filter(p => !p.isFavorite);
      if (nonFavorites.length > 0) {
        const tradeGroups: Record<string, typeof filteredPartners> = {};
        const noTrade: typeof filteredPartners = [];

        nonFavorites.forEach(partner => {
          const trade = partner.primaryTrade?.trim();
          if (!trade) {
            noTrade.push(partner);
          } else {
            if (!tradeGroups[trade]) {
              tradeGroups[trade] = [];
            }
            tradeGroups[trade].push(partner);
          }
        });

        // Sort trades alphabetically
        const sortedTrades = Object.keys(tradeGroups).sort((a, b) =>
          a.toLowerCase().localeCompare(b.toLowerCase())
        );

        sortedTrades.forEach(trade => {
          groups.push({ header: trade, partners: tradeGroups[trade] });
        });

        if (noTrade.length > 0) {
          groups.push({ header: "No Trade Listed", partners: noTrade });
        }
      }
    } else if (partnerSortBy === "name-asc" || partnerSortBy === "name-desc") {
      // Group by first letter of name
      const letterGroups: Record<string, typeof filteredPartners> = {};

      filteredPartners.forEach(partner => {
        const firstLetter = (partner.firstName?.[0] || "?").toUpperCase();
        if (!letterGroups[firstLetter]) {
          letterGroups[firstLetter] = [];
        }
        letterGroups[firstLetter].push(partner);
      });

      // Sort letters based on sort direction
      const sortedLetters = Object.keys(letterGroups).sort((a, b) =>
        partnerSortBy === "name-asc" ? a.localeCompare(b) : b.localeCompare(a)
      );

      sortedLetters.forEach(letter => {
        groups.push({ header: letter, partners: letterGroups[letter] });
      });
    } else if (partnerSortBy === "trade-asc" || partnerSortBy === "trade-desc") {
      // Group by trade
      const tradeGroups: Record<string, typeof filteredPartners> = {};
      const noTrade: typeof filteredPartners = [];

      filteredPartners.forEach(partner => {
        const trade = partner.primaryTrade?.trim();
        if (!trade) {
          noTrade.push(partner);
        } else {
          if (!tradeGroups[trade]) {
            tradeGroups[trade] = [];
          }
          tradeGroups[trade].push(partner);
        }
      });

      // Sort trades based on sort direction
      const sortedTrades = Object.keys(tradeGroups).sort((a, b) =>
        partnerSortBy === "trade-asc"
          ? a.toLowerCase().localeCompare(b.toLowerCase())
          : b.toLowerCase().localeCompare(a.toLowerCase())
      );

      sortedTrades.forEach(trade => {
        groups.push({ header: trade, partners: tradeGroups[trade] });
      });

      // Add "No Trade" group at the end if there are partners without trades
      if (noTrade.length > 0) {
        groups.push({ header: "No Trade Listed", partners: noTrade });
      }
    }

    return groups;
  }, [filteredPartners, partnerSortBy]);


  // Filter suggested partners based on search and industry filter
  const filteredSuggestedPartners = useMemo(() => {
    const suggestedArray = Array.isArray(suggestedPartners) ? suggestedPartners : [];
    let filtered = [...suggestedArray];

    // Filter out current user (safety check in case backend filter fails)
    filtered = filtered.filter((partner) => partner.id !== user?.id);

    // Filter out admin and internal accounts (Super Admin, System Auditor, Organization Member)
    const excludedRoles = ['Super Admin', 'System Auditor', 'Organization Member'];
    filtered = filtered.filter((partner) =>
      !partner.roleName || !excludedRoles.includes(partner.roleName)
    );

    // Search filter - includes name, company, and location
    if (findSearchQuery.trim()) {
      const query = findSearchQuery.toLowerCase();
      filtered = filtered.filter((partner) => {
        const fullName = `${partner.firstName} ${partner.lastName}`.toLowerCase();
        const company = (partner.company || "").toLowerCase();
        const location = (partner.location || "").toLowerCase();

        // Check name or company match
        if (fullName.includes(query) || company.includes(query)) {
          return true;
        }

        // Check location field
        if (location.includes(query)) {
          return true;
        }

        // Check serviceAreas array
        if (partner.serviceAreas && Array.isArray(partner.serviceAreas)) {
          if (partner.serviceAreas.some((area: string) =>
            area.toLowerCase().includes(query)
          )) {
            return true;
          }
        }

        return false;
      });
    }

    // Trade filter - check both trades array and primaryTrade field
    if (findTradeFilter && findTradeFilter !== "all") {
      filtered = filtered.filter((partner) => {
        const filterLower = findTradeFilter.toLowerCase();
        // Check trades array (primary)
        if (partner.trades && Array.isArray(partner.trades)) {
          if (partner.trades.some((trade: string) => trade.toLowerCase() === filterLower)) {
            return true;
          }
        }
        // Fallback to primaryTrade field
        if (partner.primaryTrade?.toLowerCase() === filterLower) {
          return true;
        }
        return false;
      });
    }

    // Service area filter now handled by findSearchQuery above

    // Market type filter - check marketTypes array
    if (findMarketTypeFilter && findMarketTypeFilter !== "all") {
      filtered = filtered.filter((partner) => {
        const filterLower = findMarketTypeFilter.toLowerCase();
        if (partner.marketTypes && Array.isArray(partner.marketTypes)) {
          return partner.marketTypes.some((mt: string) => mt.toLowerCase() === filterLower);
        }
        return false;
      });
    }

    return filtered;
  }, [suggestedPartners, findSearchQuery, findTradeFilter, findMarketTypeFilter, user?.id]);

  // Get unique trades from all partners in service areas (for filter dropdown)
  const allPartnersUniqueTrades = useMemo(() => {
    const trades = new Set<string>();
    filteredSuggestedPartners.forEach((partner) => {
      if (partner.primaryTrade) {
        trades.add(partner.primaryTrade);
      }
    });
    return Array.from(trades).sort();
  }, [filteredSuggestedPartners]);

  // Get unique service areas from all partners (for filter dropdown)
  const allPartnersUniqueServiceAreas = useMemo(() => {
    const areas = new Set<string>();
    filteredSuggestedPartners.forEach((partner) => {
      if (partner.serviceAreas && Array.isArray(partner.serviceAreas)) {
        partner.serviceAreas.forEach((area: string) => {
          areas.add(area);
        });
      }
    });
    return Array.from(areas).sort();
  }, [filteredSuggestedPartners]);

  // Get user's service areas for filtering recommended partners
  const userServiceAreas = useMemo(() => {
    if (!user?.serviceAreas || !Array.isArray(user.serviceAreas)) {
      return [];
    }
    return user.serviceAreas.map((area: string) => area.toLowerCase());
  }, [user?.serviceAreas]);

  // Resolve the current user's service-area county-keys once via the backend
  // so we can match a county selection against any city inside that county
  // (and vice versa). The query stays disabled when the user has no service
  // areas — there's nothing to resolve in that case.
  const userServiceAreasKey = useMemo(
    () => (user?.serviceAreas || []).join('||'),
    [user?.serviceAreas],
  );
  const { data: userCountyKeysData } = useQuery({
    queryKey: ['/api/locations/resolve-county-keys', userServiceAreasKey],
    enabled: Array.isArray(user?.serviceAreas) && user!.serviceAreas!.length > 0,
    staleTime: 60 * 60 * 1000, // 1h — county data only changes when the user edits their profile
    queryFn: async () => {
      const response = await api.post<{ keys: string[] }>(
        '/api/locations/resolve-county-keys',
        { areas: user?.serviceAreas || [] },
      );
      return response?.keys ?? [];
    },
  });
  const userServiceAreaCountyKeys = useMemo(
    () => userCountyKeysData ?? [],
    [userCountyKeysData],
  );

  /**
   * True when `partner.serviceAreas` overlaps the current user's service
   * areas, treating a county selection on either side as covering every city
   * inside that county.
   */
  const partnerMatchesUserAreas = useMemo(() => {
    return (partner: Partner): boolean =>
      serviceAreasOverlap({
        userKeys: userServiceAreaCountyKeys,
        userAreas: user?.serviceAreas ?? null,
        partnerKeys: partner.serviceAreaCountyKeys ?? null,
        partnerAreas: partner.serviceAreas ?? null,
      });
  }, [userServiceAreaCountyKeys, user?.serviceAreas]);

  // Extract unfilled trade categories from network progress
  const unfilledTrades = useMemo(() => {
    if (!networkProgress?.categories) return [];
    return networkProgress.categories
      .filter((cat: any) => cat.isActive && !cat.isFilled)
      .map((cat: any) => cat.name.toLowerCase());
  }, [networkProgress]);

  // Get all recommended partners (for modal and display)
  const allRecommendedPartners = useMemo(() => {
    if (userServiceAreas.length === 0) {
      return filteredSuggestedPartners;
    }

    // Filter partners who have service areas matching the user's service
    // areas. County selections on either side match any city contained inside
    // that county (and vice versa) — see `partnerMatchesUserAreas`.
    const inServiceAreas = filteredSuggestedPartners.filter(partnerMatchesUserAreas);

    // Prioritize partners with trades we don't have (fill gaps in network)
    const fillsGaps = inServiceAreas.filter((partner) =>
      partner.primaryTrade &&
      unfilledTrades.includes(partner.primaryTrade.toLowerCase())
    );

    // If we have gap-filling partners, show those first, then other service area partners
    if (fillsGaps.length > 0) {
      // Get partners in service areas that don't fill gaps
      const otherServiceAreaPartners = inServiceAreas.filter(
        (partner) => !fillsGaps.some((gp) => gp.id === partner.id)
      );
      return [...fillsGaps, ...otherServiceAreaPartners];
    }

    return inServiceAreas.length > 0 ? inServiceAreas : filteredSuggestedPartners;
  }, [filteredSuggestedPartners, userServiceAreas, unfilledTrades, partnerMatchesUserAreas]);

  // First 5 recommended partners for main display
  const recommendedPartners = useMemo(() => {
    return allRecommendedPartners.slice(0, 5);
  }, [allRecommendedPartners]);

  // All partners in service areas (excluding recommended) with filtering and sorting
  const otherPartners = useMemo(() => {
    let partners: Partner[] = [];

    if (userServiceAreas.length === 0) {
      // If user has no service areas, show all filtered partners except recommended
      const recommendedIds = new Set(recommendedPartners.map(p => p.id));
      partners = filteredSuggestedPartners.filter(p => !recommendedIds.has(p.id));
    } else {
      // Filter partners who are in the user's service areas — county
      // selections on either side cover all cities inside the county.
      const inServiceAreas = filteredSuggestedPartners.filter(partnerMatchesUserAreas);

      // Exclude recommended partners
      const recommendedIds = new Set(recommendedPartners.map(p => p.id));
      partners = inServiceAreas.filter(p => !recommendedIds.has(p.id));
    }

    // Apply trade filter
    if (allPartnersTradeFilter && allPartnersTradeFilter !== "all") {
      partners = partners.filter((partner) =>
        partner.primaryTrade?.toLowerCase() === allPartnersTradeFilter.toLowerCase()
      );
    }

    // Apply service area filter
    if (allPartnersServiceAreaFilter && allPartnersServiceAreaFilter !== "all") {
      partners = partners.filter((partner) => {
        if (!partner.serviceAreas || !Array.isArray(partner.serviceAreas)) {
          return false;
        }
        return partner.serviceAreas.some((area: string) =>
          area.toLowerCase() === allPartnersServiceAreaFilter.toLowerCase()
        );
      });
    }

    // Apply sorting
    const sorted = [...partners];
    switch (allPartnersSortBy) {
      case "a-z":
        sorted.sort((a, b) => {
          const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
          const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
          return nameA.localeCompare(nameB);
        });
        break;
      case "z-a":
        sorted.sort((a, b) => {
          const nameA = `${a.firstName} ${a.lastName}`.toLowerCase();
          const nameB = `${b.firstName} ${b.lastName}`.toLowerCase();
          return nameB.localeCompare(nameA);
        });
        break;
    }

    return sorted;
  }, [filteredSuggestedPartners, recommendedPartners, userServiceAreas, allPartnersTradeFilter, allPartnersServiceAreaFilter, allPartnersSortBy, partnerMatchesUserAreas]);


  // Mutation: Toggle favorite status
  const toggleFavoriteMutation = useMutation({
    mutationFn: async ({ partnerId, connectionId, currentStatus }: { partnerId: string; connectionId: string; currentStatus: boolean }) => {
      const newStatus = !currentStatus;

      // Optimistic update
      queryClient.setQueryData(["/api/workflows/connections"], (oldData: any) => {
        if (!Array.isArray(oldData)) return oldData;
        return oldData.map((conn: any) =>
          conn.id === partnerId
            ? { ...conn, isFavorite: newStatus }
            : conn
        );
      });

      // Update selectedProfilePartner state for real-time UI update
      setSelectedProfilePartner((prev) =>
        prev && prev.id === partnerId
          ? { ...prev, isFavorite: newStatus }
          : prev
      );

      return api.patch(`/api/workflows/connections/${connectionId}/favorite`, {
        isFavorite: newStatus
      });
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/workflows/connections"] });
      showSuccess(
        variables.currentStatus ? "Removed from Favorites" : "Added to Favorites",
        variables.currentStatus
          ? "Partner removed from your favorites"
          : "Partner added to your favorites"
      );
    },
    onError: (error: any, variables) => {
      // Revert optimistic update
      queryClient.setQueryData(["/api/workflows/connections"], (oldData: any) => {
        if (!Array.isArray(oldData)) return oldData;
        return oldData.map((conn: any) =>
          conn.id === variables.partnerId
            ? { ...conn, isFavorite: variables.currentStatus }
            : conn
        );
      });

      // Revert selectedProfilePartner state
      setSelectedProfilePartner((prev) =>
        prev && prev.id === variables.partnerId
          ? { ...prev, isFavorite: variables.currentStatus }
          : prev
      );

      showError("Update Failed", error.message || "Failed to update favorite status");
    },
  });

  // Mutation: Connect with a new partner (with optional message)
  const connectMutation = useMutation({
    mutationFn: async ({ partnerId, message }: { partnerId: string; message?: string }) => {
      // Construct payload - only include message if it has content
      const payload: { receiverId: string; message?: string } = {
        receiverId: partnerId
      };

      // Only add message field if it has actual content
      if (message && message.trim()) {
        payload.message = message.trim();
      }

      return api.post("/api/workflows/connections/request", payload);
    },
    onSuccess: (_, variables) => {
      // The profile modal may still be open underneath: flip its button to "Request Pending"
      setSelectedProfilePartner((prev) =>
        prev && prev.id === variables.partnerId ? { ...prev, status: 'pending' } : prev
      );
      queryClient.invalidateQueries({ queryKey: ["/api/workflows/connections"] });
      queryClient.invalidateQueries({ queryKey: ["/api/workflows/connections/pending"] });
      queryClient.invalidateQueries({ queryKey: ["/api/partners/suggested"] });
      showSuccess("Connection Request Sent", "Your connection request has been sent");
      setConnectDialogOpen(false);
      setSelectedPartner(null);
      setConnectionMessage("");
    },
    onError: (error: any, variables: { partnerId: string; message?: string }) => {
      // Extract detailed error message from backend
      const errorMessage = error.data?.error || error.message || "Failed to send connection request";
      const errorDetails = error.data?.details || "";

      showError("Connection Failed", errorDetails ? `${errorMessage}: ${errorDetails}` : errorMessage);

      // Log full error for debugging
      console.error("[NetworkPage] Connection request failed:", {
        partnerId: variables.partnerId,
        error,
        errorData: error.data,
        status: error.status
      });
    },
  });

  // Mutation: Accept connection request
  const acceptRequestMutation = useMutation({
    mutationFn: async (requesterId: string) => {
      return api.post("/api/workflows/connections/accept", { requesterId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workflows/connections"] });
      queryClient.invalidateQueries({ queryKey: ["/api/workflows/connections/pending"] });
      showSuccess("Connection Accepted", "You are now connected");
    },
    onError: (error: any) => {
      const errorMessage = error.data?.error || error.message || "Failed to accept connection";
      showError("Accept Failed", errorMessage);
    },
  });

  // Mutation: Decline connection request
  const declineRequestMutation = useMutation({
    mutationFn: async (requesterId: string) => {
      return api.post("/api/workflows/connections/decline", { requesterId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workflows/connections/pending"] });
      showSuccess("Request Declined", "Connection request declined");
    },
    onError: (error: any) => {
      const errorMessage = error.data?.error || error.message || "Failed to decline connection";
      showError("Decline Failed", errorMessage);
    },
  });

  // Mutation: Cancel outgoing connection request
  const cancelRequestMutation = useMutation({
    mutationFn: async (receiverId: string) => {
      // Cancel uses the same endpoint as decline - pass the other user's ID as requesterId
      return api.post("/api/workflows/connections/decline", { requesterId: receiverId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/workflows/connections/pending"] });
      queryClient.invalidateQueries({ queryKey: ["/api/partners/suggested"] });
      showSuccess("Request Canceled", "Connection request canceled");
    },
    onError: (error: any) => {
      const errorMessage = error.data?.error || error.message || "Failed to cancel connection";
      showError("Cancel Failed", errorMessage);
    },
  });

  // Block/Unblock mutations
  const blockUser = useBlockUser();
  const unblockUser = useUnblockUser();

  // Handlers
  const handleBlockUser = async (userId: string, userName: string) => {
    setUserToBlock({ id: userId, name: userName });
    setBlockDialogOpen(true);
  };

  const confirmBlockUser = async () => {
    if (!userToBlock) return;

    try {
      await blockUser.mutateAsync({ user_id: userToBlock.id });
      showSuccess("User Blocked", `${userToBlock.name} has been blocked`);
      setBlockDialogOpen(false);
      setUserToBlock(null);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || "Failed to block user";
      showError("Block Failed", errorMessage);
    }
  };

  const handleUnblockUser = async (userId: string, userName: string) => {
    try {
      await unblockUser.mutateAsync({ user_id: userId });
      showSuccess("User Unblocked", `${userName} has been unblocked`);
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || "Failed to unblock user";
      showError("Unblock Failed", errorMessage);
    }
  };

  const handleToggleFavorite = (partnerId: string) => {
    const connectionsArray = Array.isArray(connections) ? connections : [];
    const connection = connectionsArray.find((conn: any) => conn.id === partnerId);

    if (!connection) return;

    toggleFavoriteMutation.mutate({
      partnerId,
      connectionId: connection.connectionId || connection.id,
      currentStatus: connection.isFavorite || false,
    });
  };

  const handleMessagePartner = (partner: Connection | Partner) => {
    navigate('/messages', {
      state: {
        autoSelectPartnerId: partner.id,
        newConversationPartner: {
          id: partner.id,
          firstName: partner.firstName,
          lastName: partner.lastName,
          company: partner.company,
          primaryTrade: partner.primaryTrade,
          profileImageUrl: partner.profileImageUrl
        }
      }
    });
  };

  const handleSendReferral = (partner: Connection | Partner) => {
    navigate(`/send-referral?partnerId=${partner.id}`);
  };

  const handleViewProfile = (partner: Connection | Partner) => {
    setSelectedProfilePartner(partner);
    setProfilePreviewOpen(true);
  };

  const handleOpenConnectDialog = (partner: Partner) => {
    setSelectedPartner(partner);
    setConnectionMessage("");
    setConnectDialogOpen(true);
  };

  const handleConnect = () => {
    console.log("[NetworkPage] handleConnect called with selectedPartner:", selectedPartner);

    if (!selectedPartner) {
      showError("Error", "No partner selected");
      return;
    }

    // Validate partnerId exists
    if (!selectedPartner.id) {
      showError("Error", "Invalid partner ID");
      console.error("[NetworkPage] Invalid partner:", selectedPartner);
      return;
    }

    const payload = {
      partnerId: selectedPartner.id,
      message: connectionMessage.trim() || undefined
    };

    connectMutation.mutate(payload);
  };

  const clearSearch = () => {
    setSearchQuery("");
    setTradeFilter("all");
    setPartnerSortBy("favorites");
    setShowFavoritesOnly(false);
  };

  const clearFindSearch = () => {
    setFindSearchQuery("");
    setFindTradeFilter("all");
    setFindMarketTypeFilter("all");
  };

  const openEmailFallback = () => {
    const userName = user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.email || 'A Trade Loop member';

    const subject = `${userName} invited you to join Trade Loop`;

    // Build professional signature
    const signatureParts = [];
    signatureParts.push(userName);

    if (user?.primaryTrade) {
      signatureParts.push(user.primaryTrade);
    }

    if (user?.company) {
      signatureParts.push(user.company);
    }

    if (user?.email) {
      signatureParts.push(`Email: ${user.email}`);
    }

    if (user?.phone) {
      signatureParts.push(`Phone: ${user.phone}`);
    }

    const signature = signatureParts.join('\n');

    const body = `Hi there!

I'd like to invite you to join me on Trade Loop - a professional network where trade professionals connect and share referrals.

Trade Loop makes it easy to:
• Build your professional network
• Exchange qualified referrals
• Grow your business with trusted partners

Join me by clicking the link below:
${inviteLink}

Looking forward to connecting with you!

Best regards,

${signature}`;

    const mailtoLink = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    window.location.href = mailtoLink;
    showSuccess("Email Opened", "Your email client has been opened with the invitation");
  };

  const handleShare = async () => {
    const userName = user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : user?.email || 'A Trade Loop member';

    const shareData = {
      title: 'Join me on Trade Loop',
      text: `${userName} invited you to join Trade Loop - a professional network where trade professionals connect and share referrals.`,
      url: inviteLink,
    };

    // Check if Web Share API is available (usually on mobile)
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        showSuccess("Shared!", "Your invite was shared successfully");
      } catch (err: any) {
        // User cancelled or share failed
        if (err.name !== 'AbortError') {
          // Fall back to email if share fails (not just cancelled)
          openEmailFallback();
        }
      }
    } else {
      // Desktop fallback - use email
      openEmailFallback();
    }
  };

  // Render loading skeleton
  const renderLoadingSkeleton = () => (
    <Card className="bg-white border border-gray-200">
      <CardContent className="p-4">
        <div className="flex items-center space-x-3">
          <Skeleton className="w-10 h-10 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-3 w-24" />
          </div>
          <Skeleton className="h-8 w-20" />
        </div>
      </CardContent>
    </Card>
  );

  // Render partner card (clickable to view profile)
  const renderPartnerCard = (partner: Connection | Partner, isConnected: boolean) => (
    <Card
      key={partner.id}
      className="bg-white border border-gray-200 hover:shadow-md transition-all duration-200 hover:border-blue-300 cursor-pointer group"
      onClick={() => handleViewProfile(partner)}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3 flex-1">
            <Avatar className="w-10 h-10 border-2 border-gray-200 group-hover:border-blue-300 transition-colors">
              <AvatarImage src={partner.profileImageUrl || undefined} alt={`${partner.firstName} ${partner.lastName}`} />
              <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-xs font-bold">
                {partner.firstName?.[0]}{partner.lastName?.[0]}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 group-hover:text-blue-600 transition-colors flex items-center gap-1.5">
                {partner.firstName} {partner.lastName}
                <ProBadge subscriptionPlan={partner.subscriptionPlan} size="sm" />
              </h3>
              {partner.company && (
                <p className="text-sm text-gray-500">{partner.company}</p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {partner.primaryTrade && (
              <Badge variant="outline" className="text-xs">
                {partner.primaryTrade}
              </Badge>
            )}
            <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <AppLayout activeTab="network" title="Network" onRefresh={handleRefresh}>
      {/* Column cap for tablet widths — see the matching comment in ReferralsPage. These two were
          the only AppLayout pages without one, so they were the only ones that ran edge-to-edge on
          an iPad. `max-w-md` (448px) is wider than any iPhone, so phones are unaffected. */}
      <div className="max-w-md md:max-w-2xl lg:max-w-4xl mx-auto px-4 pt-[9px] md:pt-[4px] pb-[44px] md:pb-6">
        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="flex space-x-1 bg-white shadow-md border border-gray-200 rounded-2xl p-1.5">
            <button
              onClick={() => handleTabChange("partners")}
              className={`flex-1 py-3 px-4 text-sm font-semibold rounded-xl transition-all duration-300 ${
                activeTab === "partners"
                  ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md"
                  : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
              }`}
            >
              My Partners
            </button>
            <button
              onClick={() => handleTabChange("suggestions")}
              className={`flex-1 py-3 px-4 text-sm font-semibold rounded-xl transition-all duration-300 ${
                activeTab === "suggestions"
                  ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-md"
                  : "text-gray-600 hover:text-gray-800 hover:bg-gray-100"
              }`}
            >
              Find New Partners
            </button>
          </div>
        </div>

        {/* My Partners Tab */}
        {activeTab === "partners" && (
          <div>
            {/* Network Health Card - Compact with Modal */}
            <NetworkHealthCard
              userId={user?.id}
              partnerConnections={connections.length}
            />

            {/* Pending Connection Requests Section */}
            {(incomingRequests.length > 0 || outgoingRequests.length > 0) && (
              <div className="mb-6">
                {/* Incoming Requests */}
                {incomingRequests.length > 0 && (
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-3">
                      <UserCheck className="w-5 h-5 text-green-600" />
                      <h3 className="font-semibold text-gray-800">
                        Incoming Requests
                      </h3>
                      <Badge className="bg-green-100 text-green-700 border border-green-200">
                        {incomingRequests.length}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      {incomingRequests.map((request: PendingConnection) => (
                        <Card
                          key={request.id}
                          className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200"
                        >
                          <CardContent className="p-4">
                            {/* Stacked below `sm`, one row above it. This used to be a plain
                                `flex items-center justify-between`: the text block was `flex-1`
                                with no `min-w-0`, so it kept `min-width: auto` and refused to
                                give up its intrinsic width (the trade Badge beneath the name
                                cannot wrap), and the button group — which had no `shrink-0` —
                                lost the fight and was pushed off the right edge of the card,
                                where it clipped. Truncating instead is not an option here:
                                Accept + Decline is 213px on its own, and a 320px phone has only
                                254px of card left after the avatar, so the name would have had
                                nothing to render into. Same pattern as the confirmation buttons
                                on DeleteAccountPage. */}
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                              <div
                                className="flex items-center space-x-3 flex-1 min-w-0 cursor-pointer"
                                onClick={() => request.otherUser && handleViewProfile({ ...request.otherUser, status: 'pending' } as Partner)}
                              >
                                <Avatar className="w-10 h-10 border-2 border-green-200">
                                  <AvatarImage
                                    src={request.otherUser?.profileImageUrl || undefined}
                                    alt={`${request.otherUser?.firstName} ${request.otherUser?.lastName}`}
                                  />
                                  <AvatarFallback className="bg-gradient-to-br from-green-500 to-emerald-600 text-white text-xs font-bold">
                                    {request.otherUser?.firstName?.[0]}
                                    {request.otherUser?.lastName?.[0]}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-medium text-gray-900">
                                    {request.otherUser?.firstName} {request.otherUser?.lastName}
                                  </h4>
                                  <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
                                    {request.otherUser?.company && (
                                      <span>{request.otherUser.company}</span>
                                    )}
                                    {request.otherUser?.primaryTrade && (
                                      <Badge variant="outline" className="text-xs">
                                        {request.otherUser.primaryTrade}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 sm:shrink-0">
                                <Button
                                  size="sm"
                                  onClick={() => request.otherUser && acceptRequestMutation.mutate(request.otherUser.id)}
                                  disabled={acceptRequestMutation.isPending}
                                  className="flex-1 sm:flex-none bg-green-600 hover:bg-green-700 text-white"
                                >
                                  {acceptRequestMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <Check className="w-4 h-4 mr-1" />
                                      Accept
                                    </>
                                  )}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => request.otherUser && declineRequestMutation.mutate(request.otherUser.id)}
                                  disabled={declineRequestMutation.isPending}
                                  className="flex-1 sm:flex-none border-gray-300 text-gray-600 hover:bg-gray-100"
                                >
                                  {declineRequestMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <X className="w-4 h-4 mr-1" />
                                      Decline
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}

                {/* Outgoing Requests */}
                {outgoingRequests.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <Clock className="w-5 h-5 text-amber-600" />
                      <h3 className="font-semibold text-gray-800">
                        Pending Requests
                      </h3>
                      <Badge className="bg-amber-100 text-amber-700 border border-amber-200">
                        {outgoingRequests.length}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      {outgoingRequests.map((request: PendingConnection) => (
                        <Card
                          key={request.id}
                          className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200"
                        >
                          <CardContent className="p-4">
                            {/* Same shape, and the same overflow, as the incoming card above —
                                see the comment there. */}
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                              <div
                                className="flex items-center space-x-3 flex-1 min-w-0 cursor-pointer"
                                onClick={() => request.otherUser && handleViewProfile({ ...request.otherUser, status: 'pending' } as Partner)}
                              >
                                <Avatar className="w-10 h-10 border-2 border-amber-200">
                                  <AvatarImage
                                    src={request.otherUser?.profileImageUrl || undefined}
                                    alt={`${request.otherUser?.firstName} ${request.otherUser?.lastName}`}
                                  />
                                  <AvatarFallback className="bg-gradient-to-br from-amber-500 to-yellow-600 text-white text-xs font-bold">
                                    {request.otherUser?.firstName?.[0]}
                                    {request.otherUser?.lastName?.[0]}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-medium text-gray-900">
                                    {request.otherUser?.firstName} {request.otherUser?.lastName}
                                  </h4>
                                  <div className="flex flex-wrap items-center gap-2 text-sm text-gray-500">
                                    {request.otherUser?.company && (
                                      <span>{request.otherUser.company}</span>
                                    )}
                                    {request.otherUser?.primaryTrade && (
                                      <Badge variant="outline" className="text-xs">
                                        {request.otherUser.primaryTrade}
                                      </Badge>
                                    )}
                                    <span className="text-amber-600 text-xs italic">
                                      Awaiting response
                                    </span>
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 sm:shrink-0">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => request.otherUser && cancelRequestMutation.mutate(request.otherUser.id)}
                                  disabled={cancelRequestMutation.isPending}
                                  className="flex-1 sm:flex-none border-amber-300 text-amber-700 hover:bg-amber-100"
                                >
                                  {cancelRequestMutation.isPending ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <>
                                      <X className="w-4 h-4 mr-1" />
                                      Cancel
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Search and Filter Bar */}
            <div className="mb-4 pb-4 space-y-3">
              {/* Search Input - Combined name/company/location search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  type="text"
                  placeholder="Search by name, company, or location..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-20 py-3 w-full bg-white border-gray-300"
                />
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                    className={`p-1.5 h-8 w-8 transition-colors ${
                      showFavoritesOnly
                        ? "bg-yellow-100 text-yellow-600"
                        : "text-gray-400 hover:text-yellow-500"
                    }`}
                    title={
                      showFavoritesOnly
                        ? "Show all partners"
                        : "Show only favorites"
                    }
                  >
                    <Star
                      className={`w-4 h-4 ${
                        showFavoritesOnly ? "fill-current" : ""
                      }`}
                    />
                  </Button>
                  {(searchQuery || showFavoritesOnly || tradeFilter !== "all" || partnerSortBy !== "favorites") && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearSearch}
                      className="p-1.5 h-8 w-8 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Sort and Filter Row */}
              <div className="flex flex-wrap items-center gap-3">
                {/* Sort Dropdown */}
                <div className="flex items-center gap-2">
                  <ArrowUpDown className="w-4 h-4 text-gray-500" />
                  <Select value={partnerSortBy} onValueChange={setPartnerSortBy}>
                    <SelectTrigger className="w-44 bg-white">
                      <SelectValue placeholder="Sort by" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="favorites">Favorites First</SelectItem>
                      <SelectItem value="name-asc">Name (A-Z)</SelectItem>
                      <SelectItem value="name-desc">Name (Z-A)</SelectItem>
                      <SelectItem value="trade-asc">Trade (A-Z)</SelectItem>
                      <SelectItem value="trade-desc">Trade (Z-A)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Trade Filter */}
                {uniqueTrades.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-gray-500" />
                    <Select value={tradeFilter} onValueChange={setTradeFilter}>
                      <SelectTrigger className="w-44 bg-white">
                        <SelectValue placeholder="Filter by trade" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Trades</SelectItem>
                        {uniqueTrades.map((trade) => (
                          <SelectItem key={trade} value={trade.toLowerCase()}>
                            {trade}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {tradeFilter !== "all" && (
                      <Badge className="bg-blue-100 text-blue-700 border border-blue-200">
                        {tradeFilter}
                      </Badge>
                    )}
                  </div>
                )}

              </div>

              {/* Results count.
                  BUILT AS ONE STRING, NOT THREE JSX CHILDREN. Written as
                  `{n} partner{n !== 1 ? "s" : ""}` this emits three separate DOM text nodes, and
                  WKWebView exposes each as its own accessibility element — the iOS hierarchy read
                  "3", "partner", "s". VoiceOver therefore announced it in fragments, and
                  store-screenshots.yaml's non-empty check could never match: Maestro text
                  selectors match a whole node. Android's WebView flattens the <p> into one node,
                  which is why that check only ever passed there. A single interpolation gives both
                  platforms one node, the same way the neighbouring "trades covered" line already
                  does. */}
              <p className="text-sm text-gray-500">
                {`${filteredPartners.length} partner${filteredPartners.length !== 1 ? "s" : ""}${
                  searchQuery || tradeFilter !== "all" || showFavoritesOnly ? " found" : ""
                }`}
              </p>
            </div>

            {/* Partner List */}
            <div className="space-y-4">
              {connectionsLoading ? (
                // Loading state
                <>
                  {[1, 2, 3].map((i) => (
                    <div key={i}>{renderLoadingSkeleton()}</div>
                  ))}
                </>
              ) : groupedPartners.length > 0 ? (
                // Render grouped partners with section headers
                groupedPartners.map((group, groupIndex) => (
                  <div
                    key={group.header}
                    id={tradeGroupAnchorId(group.header)}
                    tabIndex={-1}
                    // The fixed app header is cleared by <main>'s scroll-padding-top
                    // (AppLayout), which knows the header's real height including the
                    // safe area. This only adds a little breathing room above the group
                    // when it is the target of a ?category= deep link.
                    className={`scroll-mt-3 focus:outline-none ${groupIndex > 0 ? "pt-2" : ""}`}
                  >
                    {/* Group Header */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex-1 h-px bg-gray-200" />
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-2">
                        {group.header}
                        <span className="ml-1.5 text-gray-400 font-normal">
                          ({group.partners.length})
                        </span>
                      </span>
                      <div className="flex-1 h-px bg-gray-200" />
                    </div>
                    {/* Partners in this group */}
                    <div className="space-y-3">
                      {group.partners.map((partner) =>
                        renderPartnerCard(partner, true)
                      )}
                    </div>
                  </div>
                ))
              ) : searchQuery || showFavoritesOnly || tradeFilter !== "all" ? (
                // No matches state
                <div className="text-center py-8 text-gray-500">
                  <Search className="mx-auto h-8 w-8 text-gray-400 mb-3" />
                  <h3 className="text-base font-medium text-gray-900 mb-2">
                    No matches found
                  </h3>
                  <p className="text-sm mb-3">
                    Try checking your spelling or clearing filters
                  </p>
                  <Button variant="outline" onClick={clearSearch}>
                    <X className="w-4 h-4 mr-2" />
                    Clear filters
                  </Button>
                </div>
              ) : (
                // Empty state
                <div className="text-center py-12 text-gray-500">
                  <Heart className="mx-auto h-16 w-16 text-gray-300 mb-6" />
                  <h3 className="text-lg font-medium text-gray-900 mb-3">
                    No Partners Connected Yet
                  </h3>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    You haven't connected with any partners yet. Once you send
                    or receive a referral, new partners will appear here
                    automatically.
                  </p>
                  <Button
                    onClick={() => handleTabChange("suggestions")}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Find New Partners
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}


        {/* Find New Partners Tab */}
        {activeTab === "suggestions" && (
          <div>
            {/* Walkthrough-only demo partners. Renders nothing outside the tour. */}
            <DemoPartnersStrip />

            {/* Search and Filter Bar */}
            <div className="mb-6 space-y-3 w-full">
              {/* Full-width search input */}
              <div className="relative block w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
                <Input
                  type="text"
                  placeholder="Search by name, company, or location..."
                  value={findSearchQuery}
                  onChange={(e) => setFindSearchQuery(e.target.value)}
                  className="block w-full pl-10 pr-10 py-3 bg-white border-gray-300"
                />
                {findSearchQuery && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFindSearchQuery("")}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1.5 h-8 w-8 text-gray-400 hover:text-gray-600"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>

              {/* Row 2: Two Select Dropdowns */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {/* Trade Type Filter */}
                <Select value={findTradeFilter} onValueChange={setFindTradeFilter}>
                  <SelectTrigger className="w-full bg-white">
                    <SelectValue placeholder="All Trades" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Trades</SelectItem>
                    {tradesLoading ? (
                      <SelectItem value="loading" disabled>
                        Loading trades...
                      </SelectItem>
                    ) : trades.map((trade) => (
                      <SelectItem key={trade} value={trade.toLowerCase()}>
                        {trade}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Market Type Filter */}
                <Select value={findMarketTypeFilter} onValueChange={setFindMarketTypeFilter}>
                  <SelectTrigger className="w-full bg-white">
                    <SelectValue placeholder="All Market Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Market Types</SelectItem>
                    {marketTypesLoading ? (
                      <SelectItem value="loading" disabled>
                        Loading market types...
                      </SelectItem>
                    ) : marketTypes.map((marketType) => (
                      <SelectItem key={marketType} value={marketType.toLowerCase()}>
                        {marketType}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Clear All Filters Button */}
              {(findSearchQuery || findTradeFilter !== "all" || findMarketTypeFilter !== "all") && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFindSearch}
                  className="text-gray-500"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear all filters
                </Button>
              )}

              {/* Results count */}
              <p className="text-sm text-gray-500">
                {filteredSuggestedPartners.length} partner{filteredSuggestedPartners.length !== 1 ? "s" : ""} available
              </p>
            </div>

            {/* Show upgrade prompt if at limit */}
            {isAtLimit('totalConnections') && (
              <div className="mb-6">
                <UpgradePrompt
                  reason="limit-reached"
                  limitType="totalConnections"
                  variant="banner"
                />
              </div>
            )}

            {/* RECOMMENDED FOR YOU Section - Partners in your service cities */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-blue-600" />
                  <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">
                    Recommended For You
                  </h3>
                </div>
                {allRecommendedPartners.length > recommendedPartners.length && (
                  <button
                    onClick={() => setRecommendedModalOpen(true)}
                    className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                  >
                    See all ({allRecommendedPartners.length})
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mb-4">
                Partners in your service cities
              </p>

              {/* Horizontal Scrolling Recommended Partners */}
              {suggestedLoading ? (
                <div className="overflow-x-auto pb-4 -mx-4 px-4">
                  <div className="flex gap-3">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex-shrink-0 w-56">
                        <Skeleton className="h-52 w-full rounded-lg" />
                      </div>
                    ))}
                  </div>
                </div>
              ) : recommendedPartners.length > 0 ? (
                <div className="overflow-x-auto pb-4 -mx-4 px-4">
                  <div className="flex gap-3">
                    {recommendedPartners.map((partner: Partner) => (
                      <Card
                        key={partner.id}
                        className="flex-shrink-0 w-56 h-[228px] bg-white border border-gray-200 hover:shadow-md hover:border-blue-300 transition-all duration-200 cursor-pointer group overflow-hidden relative"
                        onClick={() => handleViewProfile(partner)}
                      >
                        {/* Pro Badge - Top Left Corner */}
                        <div className="absolute top-2 left-2 z-10">
                          <ProBadge subscriptionPlan={partner.subscriptionPlan} size="sm" />
                        </div>
                        <CardContent className="p-4 h-full flex flex-col items-center text-center">
                          {/* Avatar */}
                          <Avatar className="w-12 h-12 mb-2 border-2 border-gray-200 group-hover:border-blue-300 transition-colors">
                            <AvatarImage src={partner.profileImageUrl || undefined} alt={`${partner.firstName} ${partner.lastName}`} />
                            <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-sm font-bold">
                              {partner.firstName?.[0]}{partner.lastName?.[0]}
                            </AvatarFallback>
                          </Avatar>

                          {/* Name */}
                          <h3 className="font-semibold text-gray-900 text-sm mb-1 group-hover:text-blue-600 transition-colors line-clamp-1">
                            {partner.firstName} {partner.lastName}
                          </h3>

                          {/* Company */}
                          {partner.company && (
                            <p className="text-xs text-gray-500 line-clamp-1 mb-1">
                              {partner.company}
                            </p>
                          )}

                          {/* Trade */}
                          {partner.primaryTrade && (
                            <p className="text-xs text-gray-600 font-medium line-clamp-1 mb-1">
                              {partner.primaryTrade}
                            </p>
                          )}

                          {/* Completed Referrals - Pro users only */}
                          {partner.subscriptionPlan === 'pro' && partner.completedReferralsCount !== undefined && partner.completedReferralsCount > 0 && (
                            <div className="flex items-center justify-center gap-1 text-xs text-emerald-600 font-medium mb-1">
                              <Check className="w-3 h-3 flex-shrink-0" />
                              <span>{partner.completedReferralsCount} referral{partner.completedReferralsCount !== 1 ? 's' : ''} completed</span>
                            </div>
                          )}

                          {/* Spacer to push button to bottom */}
                          <div className="flex-1 min-h-3" />

                          {/* Request Button */}
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenConnectDialog(partner);
                            }}
                            className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white text-xs"
                          >
                            <UserPlus className="w-3 h-3 mr-1" />
                            Request
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>

            {/* ALL PARTNERS IN YOUR AREA Section - Vertical scrolling list */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-gray-700" />
                  <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wide">
                    All Partners In Your Area
                  </h3>
                </div>
              </div>

              {/* Filters Row */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                {/* Trade Filter */}
                <Select value={allPartnersTradeFilter} onValueChange={setAllPartnersTradeFilter}>
                  <SelectTrigger className="w-auto text-xs">
                    <SelectValue placeholder="By Trade" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Trades</SelectItem>
                    {allPartnersUniqueTrades.map((trade) => (
                      <SelectItem key={trade} value={trade}>
                        {trade}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Service Area Filter */}
                <Select value={allPartnersServiceAreaFilter} onValueChange={setAllPartnersServiceAreaFilter}>
                  <SelectTrigger className="w-auto text-xs">
                    <SelectValue placeholder="By Service Area" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Service Areas</SelectItem>
                    {(user?.serviceAreas || []).map((area) => (
                      <SelectItem key={area} value={area.toLowerCase()}>
                        {area}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Sort Filter */}
                <Select value={allPartnersSortBy} onValueChange={setAllPartnersSortBy}>
                  <SelectTrigger className="w-auto text-xs">
                    <SelectValue placeholder="Sort" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="a-z">A-Z</SelectItem>
                    <SelectItem value="z-a">Z-A</SelectItem>
                  </SelectContent>
                </Select>

                {/* Partner Count */}
                <p className="text-xs text-gray-500 ml-auto">
                  {otherPartners.length} partner{otherPartners.length !== 1 ? 's' : ''}
                </p>
              </div>

              {/* Vertical List of Partners - Compact Cards */}
              {suggestedLoading ? (
                // Loading state
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-20 w-full rounded-lg" />
                  ))}
                </div>
              ) : otherPartners.length > 0 ? (
                <div className="space-y-3">
                  {otherPartners.map((partner: Partner) => (
                    <Card
                      key={partner.id}
                      className="bg-white border border-gray-200 hover:shadow-md hover:border-blue-300 transition-all duration-200 cursor-pointer group relative"
                      onClick={() => handleViewProfile(partner)}
                    >
                      {/* Pro Badge - Top Left Corner */}
                      <div className="absolute top-2 left-2 z-10">
                        <ProBadge subscriptionPlan={partner.subscriptionPlan} size="sm" />
                      </div>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          {/* Left: Avatar and Info */}
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <Avatar className="w-12 h-12 border-2 border-gray-200 group-hover:border-blue-300 transition-colors flex-shrink-0 ml-12">
                              <AvatarImage src={partner.profileImageUrl || undefined} alt={`${partner.firstName} ${partner.lastName}`} />
                              <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-sm font-bold">
                                {partner.firstName?.[0]}{partner.lastName?.[0]}
                              </AvatarFallback>
                            </Avatar>

                            <div className="flex-1 min-w-0">
                              {/* Name */}
                              <h3 className="font-semibold text-gray-900 text-sm group-hover:text-blue-600 transition-colors truncate">
                                {partner.firstName} {partner.lastName}
                              </h3>

                              {/* Company */}
                              {partner.company && (
                                <div className="text-xs text-gray-500">
                                  <span className="truncate">{partner.company}</span>
                                </div>
                              )}

                              {/* Trade */}
                              {partner.primaryTrade && (
                                <div className="text-xs text-gray-600 font-medium mt-0.5">
                                  <span className="truncate">{partner.primaryTrade}</span>
                                </div>
                              )}

                              {/* Completed Referrals - Pro users only */}
                              {partner.subscriptionPlan === 'pro' && partner.completedReferralsCount !== undefined && partner.completedReferralsCount > 0 && (
                                <div className="flex items-center gap-1 text-xs text-emerald-600 font-medium mt-0.5">
                                  <Check className="w-3 h-3 flex-shrink-0" />
                                  <span>{partner.completedReferralsCount} referral{partner.completedReferralsCount !== 1 ? 's' : ''} completed</span>
                                </div>
                              )}

                              {/* Service Areas */}
                              {partner.serviceAreas && Array.isArray(partner.serviceAreas) && partner.serviceAreas.length > 0 && (
                                <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                                  <MapPin className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate">
                                    {partner.serviceAreas.slice(0, 2).join(', ')}
                                    {partner.serviceAreas.length > 2 && ` +${partner.serviceAreas.length - 2}`}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Right: Connect Button */}
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenConnectDialog(partner);
                            }}
                            className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white text-xs px-4 flex-shrink-0"
                          >
                            <UserPlus className="w-3 h-3 mr-1" />
                            Request
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : recommendedPartners.length > 0 && otherPartners.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="mx-auto h-12 w-12 text-gray-300 mb-3" />
                  <p className="text-sm text-gray-600">
                    All partners shown in the recommended section above
                  </p>
                </div>
              ) : findSearchQuery || findTradeFilter !== "all" || findMarketTypeFilter !== "all" ? (
              // No search results
              <div className="text-center py-12 text-gray-500">
                <Search className="mx-auto h-8 w-8 text-gray-400 mb-3" />
                <h3 className="text-base font-medium text-gray-900 mb-2">
                  No matches found
                </h3>
                <p className="text-sm mb-3">
                  Try a different search term or filter
                </p>
                <Button variant="outline" onClick={clearFindSearch}>
                  <X className="w-4 h-4 mr-2" />
                  Clear filters
                </Button>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Sparkles className="mx-auto h-16 w-16 text-gray-300 mb-6" />
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  No New Partners Available
                </h3>
                <p className="text-gray-600 mb-6 max-w-md mx-auto">
                  No new public partners available at the moment. Invite someone to join Trade Loop!
                </p>
              </div>
            )}
            </div>

            {/* Invite Section */}
            <Card className="mt-6 shadow-md border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200 px-4 py-3">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  Invite Someone to Trade Loop
                </h3>
              </div>
              <CardContent className="p-6">
                <p className="text-sm text-gray-600 mb-4">
                  Know a trade professional who would be a great addition to your network?
                  Share your invite link or QR code.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to="/qr" className="flex-1">
                    <Button
                      variant="outline"
                      className="w-full"
                    >
                      <QrCode className="w-4 h-4 mr-2" />
                      Show QR Code
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={handleShare}
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

      </div>

      {/* Block Confirmation Dialog */}
      <Dialog open={blockDialogOpen} onOpenChange={setBlockDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Block {userToBlock?.name}?</DialogTitle>
            <DialogDescription>
              Are you sure you want to block this user? They will no longer be able to see you in partner suggestions or send you connection requests.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">
                <strong>Note:</strong> Blocking is unidirectional - you will still be able to see them in your searches, but they won't see you.
              </p>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setBlockDialogOpen(false);
                setUserToBlock(null);
              }}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmBlockUser}
              disabled={blockUser.isLoading}
              className="w-full sm:w-auto bg-red-600 hover:bg-red-700"
            >
              <Ban className="w-4 h-4 mr-2" />
              {blockUser.isLoading ? "Blocking..." : "Block User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Connection Request Dialog */}
      <Dialog open={connectDialogOpen} onOpenChange={setConnectDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Connect with {selectedPartner?.firstName}</DialogTitle>
            <DialogDescription>
              Send a connection request to {selectedPartner?.firstName} {selectedPartner?.lastName}.
              Add a message to introduce yourself.
            </DialogDescription>
          </DialogHeader>

          {selectedPartner && (
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg mb-4">
              <Avatar className="w-12 h-12 border-2 border-gray-200">
                <AvatarImage src={selectedPartner.profileImageUrl || undefined} alt={`${selectedPartner.firstName} ${selectedPartner.lastName}`} />
                <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-sm font-bold">
                  {selectedPartner.firstName?.[0]}{selectedPartner.lastName?.[0]}
                </AvatarFallback>
              </Avatar>
              <div>
                <h4 className="font-medium text-gray-900">
                  {selectedPartner.firstName} {selectedPartner.lastName}
                </h4>
                <div className="flex items-center gap-2">
                  {selectedPartner.company && (
                    <span className="text-sm text-gray-500">{selectedPartner.company}</span>
                  )}
                  {selectedPartner.trade && (
                    <Badge variant="outline" className="text-xs">
                      {selectedPartner.trade}
                    </Badge>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor="message" className="text-sm font-medium text-gray-700">
              Message (optional)
            </label>
            <Textarea
              id="message"
              placeholder="Hi! I saw your profile and would love to connect. I'm a plumber in the Dallas area and always looking to partner with great HVAC contractors..."
              value={connectionMessage}
              onChange={(e) => setConnectionMessage(e.target.value)}
              className="min-h-[100px]"
            />
            <p className="text-xs text-gray-500">
              A personalized message increases your chances of connecting.
            </p>
          </div>

          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setConnectDialogOpen(false)}
              disabled={connectMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConnect}
              disabled={connectMutation.isPending || !canPerformAction('totalConnections')}
              className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {connectMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Send Request
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Profile Preview Modal */}
      <ProfilePreviewModal
        isOpen={profilePreviewOpen}
        onClose={() => {
          setProfilePreviewOpen(false);
          setSelectedProfilePartner(null);
        }}
        partner={selectedProfilePartner}
        currentUserOrganizationId={user?.organizationId}
        onToggleFavorite={
          selectedProfilePartner
            ? () => handleToggleFavorite(selectedProfilePartner.id)
            : undefined
        }
        showFavoriteButton={selectedProfilePartner?.status === 'accepted' || selectedProfilePartner?.status === 'connected'}
        showMenuButton={true}
        onSendReferral={
          selectedProfilePartner
            ? () => {
                handleSendReferral(selectedProfilePartner);
                setProfilePreviewOpen(false);
                setSelectedProfilePartner(null);
              }
            : undefined
        }
        onSendMessage={
          selectedProfilePartner
            ? () => {
                handleMessagePartner(selectedProfilePartner);
                setProfilePreviewOpen(false);
                setSelectedProfilePartner(null);
              }
            : undefined
        }
        showActionButtons={selectedProfilePartner?.status === 'accepted' || selectedProfilePartner?.status === 'connected'}
        onRequestConnection={
          selectedProfilePartner
            ? () => handleOpenConnectDialog(selectedProfilePartner as Partner)
            : undefined
        }
        onPartnerRemoved={() => {
          // Refresh the connections list when a partner is removed
          queryClient.invalidateQueries({ queryKey: ["/api/workflows/connections"] });
          queryClient.invalidateQueries({ queryKey: ["/api/partners/suggested"] });
        }}
      />

      {/* Recommended Partners Modal */}
      <Dialog open={recommendedModalOpen} onOpenChange={setRecommendedModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-blue-600" />
              Recommended For You
            </DialogTitle>
            <DialogDescription>
              Partners in your service areas that fill gaps in your network
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 mt-4">
            {allRecommendedPartners.map((partner: Partner) => (
              <Card
                key={partner.id}
                className="bg-white border border-gray-200 hover:shadow-md hover:border-blue-300 transition-all duration-200 cursor-pointer group relative"
                onClick={() => {
                  handleViewProfile(partner);
                  setRecommendedModalOpen(false);
                }}
              >
                {/* Pro Badge - Top Left Corner */}
                <div className="absolute top-2 left-2 z-10">
                  <ProBadge subscriptionPlan={partner.subscriptionPlan} size="sm" />
                </div>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    {/* Left: Avatar and Info */}
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Avatar className="w-12 h-12 border-2 border-gray-200 group-hover:border-blue-300 transition-colors flex-shrink-0 ml-12">
                        <AvatarImage src={partner.profileImageUrl || undefined} alt={`${partner.firstName} ${partner.lastName}`} />
                        <AvatarFallback className="bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-sm font-bold">
                          {partner.firstName?.[0]}{partner.lastName?.[0]}
                        </AvatarFallback>
                      </Avatar>

                      <div className="flex-1 min-w-0">
                        {/* Name */}
                        <h3 className="font-semibold text-gray-900 text-sm group-hover:text-blue-600 transition-colors truncate">
                          {partner.firstName} {partner.lastName}
                        </h3>

                        {/* Company */}
                        {partner.company && (
                          <div className="text-xs text-gray-500">
                            <span className="truncate">{partner.company}</span>
                          </div>
                        )}

                        {/* Trade */}
                        {partner.primaryTrade && (
                          <div className="text-xs text-gray-600 font-medium mt-0.5">
                            <span className="truncate">{partner.primaryTrade}</span>
                          </div>
                        )}

                        {/* Completed Referrals - Pro users only */}
                        {partner.subscriptionPlan === 'pro' && partner.completedReferralsCount !== undefined && partner.completedReferralsCount > 0 && (
                          <div className="flex items-center gap-1 text-xs text-emerald-600 font-medium mt-0.5">
                            <Check className="w-3 h-3 flex-shrink-0" />
                            <span>{partner.completedReferralsCount} referral{partner.completedReferralsCount !== 1 ? 's' : ''} completed</span>
                          </div>
                        )}

                        {/* Service Areas */}
                        {partner.serviceAreas && Array.isArray(partner.serviceAreas) && partner.serviceAreas.length > 0 && (
                          <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">
                              {partner.serviceAreas.slice(0, 2).join(', ')}
                              {partner.serviceAreas.length > 2 && ` +${partner.serviceAreas.length - 2}`}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Right: Connect Button */}
                    <Button
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenConnectDialog(partner);
                        setRecommendedModalOpen(false);
                      }}
                      className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white text-xs px-4 flex-shrink-0"
                    >
                      <UserPlus className="w-3 h-3 mr-1" />
                      Request
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
